import { useId, useRef, type ReactNode } from 'react';
import { Alert, type AlertTone } from '../alert';
import { buttonClass } from '../button-class';
import { Icon } from '../icon';
import { Checklist } from './checklist';
import { errorClass, inputClass, labelClass } from './input';
import {
  EMPTY_IMAGE,
  type FormFieldSchema,
  type FormMode,
  type FormSchema,
  type FormValue,
  type ImageFieldValue,
} from './schema';
import { isFieldVisible } from './validate';

export type SchemaFieldsLayout = 'vertical' | 'horizontal';

/** Bootstrap has no neutral alert; a default notice reads as information. */
const NOTICE: Record<NonNullable<FormFieldSchema['noticeVariant']>, AlertTone> = {
  default: 'info',
  info: 'info',
  warning: 'warning',
  danger: 'danger',
};

function Repeater({
  field,
  rows,
  disabled,
  onChange,
}: {
  field: FormFieldSchema;
  rows: Record<string, unknown>[];
  disabled: boolean;
  onChange: (rows: Record<string, unknown>[]) => void;
}) {
  const items = field.itemFields ?? [];
  const blank = () =>
    Object.fromEntries(
      items.map(item => [item.key, item.defaultValue ?? (item.type === 'number' ? null : '')]),
    );
  return (
    <div className="space-y-2">
      {rows.length === 0 && field.emptyLabel ? (
        <p className="text-sm text-ink-muted">{field.emptyLabel}</p>
      ) : null}
      {rows.map((row, index) => (
        <div key={index} className="flex items-end gap-2">
          {items.map(item => (
            <label key={item.key} className="mb-0 flex-1 text-xs text-ink">
              {item.label}
              <input
                type={item.type === 'number' ? 'number' : 'text'}
                className={inputClass}
                disabled={disabled}
                min={item.min}
                max={item.max}
                placeholder={item.placeholder}
                value={
                  row[item.key] === null || row[item.key] === undefined ? '' : String(row[item.key])
                }
                onChange={event => {
                  const raw = event.target.value;
                  const next = [...rows];
                  next[index] = {
                    ...row,
                    [item.key]: item.type === 'number' ? (raw === '' ? null : Number(raw)) : raw,
                  };
                  onChange(next);
                }}
              />
            </label>
          ))}
          <button
            type="button"
            disabled={disabled}
            aria-label={`${field.label} ${index + 1}`}
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            className={buttonClass({ variant: 'danger', iconOnly: true })}
          >
            <Icon name="trash" />
          </button>
        </div>
      ))}
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange([...rows, blank()])}
        className={buttonClass({ variant: 'secondary', size: 'sm' })}
      >
        <Icon name="plus" />
        {field.addButtonLabel ?? field.label}
      </button>
    </div>
  );
}

/** Read a picked file into the field's value (the caller uploads it after saving). */
function readImage(file: File): Promise<ImageFieldValue> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      resolve({
        preview: dataUrl,
        base64: dataUrl.slice(dataUrl.indexOf(',') + 1),
        contentType: file.type || 'application/octet-stream',
        removeImage: false,
      });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function ImageField({
  id,
  field,
  value,
  disabled,
  onChange,
}: {
  id: string;
  field: FormFieldSchema;
  value: ImageFieldValue;
  disabled: boolean;
  onChange: (value: ImageFieldValue) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const labels = field.imageLabels ?? {
    select: field.label,
    replace: field.label,
    remove: field.label,
  };
  return (
    <div className="flex items-center gap-4">
      {value.preview ? (
        <img
          src={value.preview}
          alt=""
          className="h-16 w-24 rounded-bs border border-line object-contain"
        />
      ) : null}
      <input
        ref={input}
        id={id}
        type="file"
        accept={field.accept ?? 'image/*'}
        className="sr-only"
        disabled={disabled}
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) {
            void readImage(file).then(onChange, () => undefined);
          }
        }}
      />
      <button
        type="button"
        disabled={disabled}
        onClick={() => input.current?.click()}
        className={buttonClass({ variant: 'secondary', size: 'sm' })}
      >
        {value.preview ? labels.replace : labels.select}
      </button>
      {value.preview ? (
        <button
          type="button"
          disabled={disabled}
          // Removing a stored image (no file picked) is what the caller must delete.
          onClick={() =>
            onChange({ ...EMPTY_IMAGE, removeImage: value.removeImage || value.base64 === null })
          }
          className={
            buttonClass({ variant: 'link', size: 'sm' }) + ' text-danger hover:text-danger-hover'
          }
        >
          {labels.remove}
        </button>
      ) : null}
    </div>
  );
}

function Field({
  field,
  value,
  error,
  disabled,
  layout,
  onChange,
  onBlur,
}: {
  field: FormFieldSchema;
  value: unknown;
  error: string | null;
  disabled: boolean;
  layout: SchemaFieldsLayout;
  onChange: (value: unknown) => void;
  onBlur: () => void;
}) {
  const id = useId();
  const errorId = `${id}-error`;
  const invalid = error ? true : undefined;
  const described = error ? errorId : undefined;
  const text = value === null || value === undefined ? '' : String(value);

  if (field.type === 'notice') {
    return (
      <Alert tone={NOTICE[field.noticeVariant ?? 'default']} className="mb-0">
        {field.content ?? field.label}
      </Alert>
    );
  }

  let control: ReactNode;
  switch (field.type) {
    case 'textarea':
      control = (
        <textarea
          id={id}
          rows={4}
          className={inputClass}
          value={text}
          disabled={disabled}
          placeholder={field.placeholder}
          aria-invalid={invalid}
          aria-describedby={described}
          onChange={event => onChange(event.target.value)}
          onBlur={onBlur}
        />
      );
      break;
    case 'select':
      control = (
        <select
          id={id}
          className={inputClass}
          value={text}
          disabled={disabled}
          aria-invalid={invalid}
          aria-describedby={described}
          onChange={event => onChange(event.target.value)}
          onBlur={onBlur}
        >
          <option value="">{field.placeholder ?? ''}</option>
          {(field.options ?? []).map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
      break;
    case 'checkbox':
      return (
        <div>
          <label className="mb-0 flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={value === true}
              disabled={disabled}
              aria-invalid={invalid}
              aria-describedby={described}
              onChange={event => onChange(event.target.checked)}
              onBlur={onBlur}
              className="h-4 w-4 accent-brand"
            />
            {field.label}
          </label>
          {error ? (
            <p id={errorId} className={errorClass}>
              {error}
            </p>
          ) : null}
        </div>
      );
    case 'radio':
      return (
        <fieldset aria-describedby={described}>
          <legend className={labelClass}>{field.label}</legend>
          <div className="flex flex-wrap gap-4">
            {(field.options ?? []).map(option => (
              <label key={option.value} className="mb-0 flex items-center gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name={field.key}
                  value={option.value}
                  checked={text === option.value}
                  className="accent-brand"
                  disabled={disabled}
                  onChange={() => onChange(option.value)}
                  onBlur={onBlur}
                />
                {option.label}
              </label>
            ))}
          </div>
          {error ? (
            <p id={errorId} className={errorClass}>
              {error}
            </p>
          ) : null}
        </fieldset>
      );
    case 'checklist':
      control = (
        <Checklist
          options={field.options ?? []}
          value={Array.isArray(value) ? (value as string[]) : []}
          onChange={onChange}
          searchable={field.searchable}
          searchPlaceholder={field.searchPlaceholder}
          disabled={disabled}
          label={field.label}
        />
      );
      break;
    case 'image':
      control = (
        <ImageField
          id={id}
          field={field}
          value={(value as ImageFieldValue | undefined) ?? EMPTY_IMAGE}
          disabled={disabled}
          onChange={onChange}
        />
      );
      break;
    case 'repeater':
      control = (
        <Repeater
          field={field}
          rows={Array.isArray(value) ? (value as Record<string, unknown>[]) : []}
          disabled={disabled}
          onChange={onChange}
        />
      );
      break;
    default:
      control = (
        <input
          id={id}
          type={field.type === 'number' ? 'number' : field.type}
          className={inputClass}
          value={text}
          disabled={disabled}
          placeholder={field.placeholder}
          min={field.min}
          max={field.max}
          aria-invalid={invalid}
          aria-describedby={described}
          onChange={event => {
            const raw = event.target.value;
            onChange(field.type === 'number' ? (raw === '' ? null : Number(raw)) : raw);
          }}
          onBlur={onBlur}
        />
      );
  }

  const label = (
    <label
      htmlFor={id}
      className={`${labelClass} ${layout === 'horizontal' ? 'md:mb-0 md:pt-2' : ''}`}
    >
      {field.label}
      {field.required ? <span aria-hidden="true"> *</span> : null}
    </label>
  );
  const message = error ? (
    <p id={errorId} className={errorClass}>
      {error}
    </p>
  ) : null;

  // Horizontal (`.form-group.row`): label a third, control two thirds, from `md` up.
  return layout === 'horizontal' ? (
    <div className="md:grid md:grid-cols-3 md:gap-4">
      {label}
      <div className="md:col-span-2">
        {control}
        {message}
      </div>
    </div>
  ) : (
    <div>
      {label}
      {control}
      {message}
    </div>
  );
}

/**
 * The fields of a schema, sections titled. `vertical` (default) is a two-column grid of
 * label-over-control fields; `horizontal` is one field per row with the label beside the control.
 * Controlled: the caller holds the values and decides which errors to show (see `ModalForm`).
 */
export function SchemaFields({
  schema,
  mode,
  value,
  errors,
  disabled = false,
  layout = 'vertical',
  onChange,
  onBlur,
}: {
  schema: FormSchema;
  mode: FormMode;
  value: FormValue;
  /** Errors to show, by field key. */
  errors: Record<string, string>;
  disabled?: boolean;
  layout?: SchemaFieldsLayout;
  onChange: (key: string, value: unknown) => void;
  onBlur: (key: string) => void;
}) {
  const visible = schema.fields.filter(field => isFieldVisible(field, mode, value));
  const inSections = new Set((schema.sections ?? []).flatMap(section => section.fieldKeys));
  const render = (fields: FormFieldSchema[]) => (
    <div className={`grid grid-cols-1 gap-4 ${layout === 'vertical' ? 'sm:grid-cols-2' : ''}`}>
      {fields.map(field => (
        <div
          key={field.key}
          className={
            layout === 'vertical' &&
            (field.fullWidth ||
              ['textarea', 'checklist', 'repeater', 'notice'].includes(field.type))
              ? 'sm:col-span-2'
              : ''
          }
        >
          <Field
            field={field}
            value={value[field.key]}
            error={errors[field.key] ?? null}
            disabled={disabled || (mode === 'edit' && field.disabledWhenEditing === true)}
            layout={layout}
            onChange={next => onChange(field.key, next)}
            onBlur={() => onBlur(field.key)}
          />
        </div>
      ))}
    </div>
  );
  return (
    <div className="space-y-6">
      {render(visible.filter(field => !inSections.has(field.key)))}
      {(schema.sections ?? []).map(section => {
        const fields = visible.filter(field => section.fieldKeys.includes(field.key));
        return fields.length === 0 ? null : (
          <section key={section.sectionTitle}>
            <h3 className="mb-4 rounded-bs border border-line bg-cap px-5 py-3 text-sm leading-normal font-bold text-ink">
              {section.sectionTitle}
            </h3>
            {render(fields)}
          </section>
        );
      })}
    </div>
  );
}
